<template>
  <single-page>
    <template #breadcrumbs>
      <router-link :to="{ name: 'admin-venue-groups' }">
        <font-awesome-icon icon="arrow-left"/>
        <span>Back to {{ $helpers.remapNaming('venues') }}</span>
      </router-link>
    </template>
    <template #title>
      <v-entity-title :entity="group" />
    </template>
    <template #actions>
      <ez-button
        v-if="canSendInvite"
        type="primary"
        customClass="invitation-button"
        @click="sendInvitation">
        <font-awesome-icon icon="paper-plane"></font-awesome-icon>
        {{ invitationCta }}
      </ez-button>
      <ez-button-dropdown buttonType="secondary">
        <template #icon>
          <font-awesome-icon icon="ellipsis-h"/>
        </template>
        <template #dropdown>
          <ez-button
            @click="onDeleteGroup"
            type="red-link">Delete Group</ez-button>
        </template>
      </ez-button-dropdown>
    </template>
    <template #navigation>
      <ul>
        <li>
          <router-link
            :to="{ name: 'admin-venue-groups-info' }">Group Info</router-link>
        </li>
        <li>
          <router-link
            :to="{ name: 'admin-venue-groups-venues' }">
            {{ $helpers.remapNaming('venues') }}
          </router-link>
        </li>
        <li>
          <router-link
            :to="{ name: 'admin-venue-groups-users' }">Users</router-link>
        </li>
      </ul>
    </template>
    <ez-confirmation-modal ref="deleteConfirmation" type="red">
      <template #title>
        Delete this {{ $helpers.remapNaming('venue', 'lower') }} group?
      </template>
      <template #content>
        <p>When deleted, this {{ $helpers.remapNaming('venue', 'lower') }}
          group will no longer be live on OrderEZ.</p>

        <p>You won’t see this {{ $helpers.remapNaming('venue', 'lower') }}
          group in admin panel as well.</p>
      </template>
      <template #footer>
        <ez-button @click="closeConfirmationModal" type="link">Cancel</ez-button>
        <ez-button @click="confirmDelete" type="red">Yes, Delete</ez-button>
      </template>
    </ez-confirmation-modal>
  </single-page>
</template>

<script>
import { mapActions } from 'vuex';
import flash from '@/components/ui/FlashMessage';
import EzButton from '@/components/ui/Button';
import EzButtonDropdown from '@/components/ui/ButtonDropdown';
import { EzConfirmationModal } from '@/components/ui/Modal';
import VEntityTitle from '@/components/v3/patterns/VEntityTitle';
import SinglePage from '@/views/admin/SinglePage.vue';
import Group from '@/models/Group';
import { ACCOUNT_STATUS_INVITE_SENT, ACCOUNT_STATUS_DRAFT } from '@/util/constants';

export default {
  components: {
    EzButton,
    EzButtonDropdown,
    EzConfirmationModal,
    VEntityTitle,
    SinglePage,
  },
  props: {
    groupId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    canSendInvite() {
      return [ACCOUNT_STATUS_INVITE_SENT, ACCOUNT_STATUS_DRAFT].includes(this.group.status);
    },
    invitationCta() {
      return this.group.status === ACCOUNT_STATUS_DRAFT ? 'Send Invite' : 'Resend Invite';
    },
    group() {
      return Group.query()
        .with('venues')
        .find(this.groupId) || { venues: [] };
    },
  },
  methods: {
    onDeleteGroup() {
      this.$refs.deleteConfirmation.open();
    },
    closeConfirmationModal() {
      this.$refs.deleteConfirmation.close();
    },
    confirmDelete() {
      this.removeGroup(this.groupId)
        .then(() => {
          this.$router.replace({
            name: 'admin-venue-groups',
            flash: {
              title: `${this.$helpers.remapNaming('venue')} group deleted.`,
              type: 'success',
            },
          });
        });
    },
    sendInvitation() {
      this.sendGroupInvitation(this.groupId)
        .catch(({ response }) => {
          flash.error({
            title: 'Invitation sending failed!',
            message: response.data.error.message,
          });
        });
    },
    ...mapActions('entities/groups', [
      'fetchGroup',
      'removeGroup',
      'sendGroupInvitation',
    ]),
  },
  async mounted() {
    await this.fetchGroup(this.groupId);
  },
};
</script>
<style scoped lang="scss">
:deep() .admin-single-page {
  &__title {
    margin-bottom: .5rem;
  }
}
</style>
